import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import * as DateFns from "date-fns"

import * as Commands from "../../../../../model/commands"
import { ID, SourceStatus, WebSource } from "../../../../../model/types"
import api from "../../../../../services/api"
import { useNana } from "../../../../hooks/useNana"
import useFlash from "../../../../hooks/use_flash"
import { SourcesPageState } from "../../sources_page"
import SourceStatusTag from "../../sources_page/source_status_tag"
import MoveCard, {
  CardMeta,
  CardPrimaryButton,
  CardSecondaryButton,
  sourceDisplayTitle,
} from "../move_card"

const hostFromUrl = (url: string | null | undefined): string | null => {
  if (!url) return null
  try {
    return new URL(url).hostname.replace(/^www\./, "")
  } catch {
    return null
  }
}

export default function ReadCard({
  sourceId,
  onReroll,
  onObjectGone,
}: {
  sourceId: ID
  onReroll: () => void
  onObjectGone: () => void
}) {
  const { model, commander } = useNana()
  const navigate = useNavigate()
  const { setFlash } = useFlash()
  const [rescanning, setRescanning] = useState(false)
  const source = model.getSource(sourceId)

  useEffect(() => {
    if (!source) onObjectGone()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source])

  if (!source) return null

  const openReading = () => navigate(`/app/reading/${source.id}`)

  const openSourcePage = () => {
    SourcesPageState.openSourceId = source.id
    navigate("/app/sources")
  }

  const setStatus = (status: SourceStatus) => {
    commander.exec(
      new Commands.SetWebSourceFieldCommand({
        id: source.id,
        field: "status",
        value: status,
      }),
    )
    if (status === "read") onObjectGone()
    else onReroll()
  }

  const rescanMetadata = async () => {
    if (rescanning) return
    setRescanning(true)
    try {
      await api().post(`/api/sources/${source.id}/metadata/reprocess`)
      setFlash({
        type: "success",
        text: "Metadata rescan queued",
      })
    } catch {
      setFlash({
        type: "error",
        text: "Could not queue metadata rescan",
      })
    } finally {
      setRescanning(false)
    }
  }

  const title = sourceDisplayTitle(source)
  const host = hostFromUrl(source.url)
  const titleIsHostOnly = host != null && title === host

  return (
    <MoveCard
      move="read"
      onReroll={onReroll}
      onPrimary={openReading}
      actions={
        <>
          <CardPrimaryButton onClick={openReading}>Reading view</CardPrimaryButton>
          <CardSecondaryButton onClick={openSourcePage}>
            Source page
          </CardSecondaryButton>
          <CardSecondaryButton onClick={rescanMetadata}>
            {rescanning ? "Rescanning…" : "Rescan metadata"}
          </CardSecondaryButton>
          {source.status === "queued" && (
            <CardSecondaryButton onClick={() => setStatus("new")}>
              Dequeue
            </CardSecondaryButton>
          )}
          {source.status === "new" && (
            <CardSecondaryButton onClick={() => setStatus("queued")}>
              Queue
            </CardSecondaryButton>
          )}
          {(source.status === "new" || source.status === "queued") && (
            <CardSecondaryButton onClick={() => setStatus("read")}>
              Mark read
            </CardSecondaryButton>
          )}
        </>
      }
    >
      <div className="flex min-h-[7rem] flex-1 flex-col">
        <h3 className="line-clamp-3 break-words text-[1.2rem] font-semibold leading-snug tracking-[-0.02em] text-stone-900 dark:text-white">
          {title}
        </h3>
        {(source.author || source.publicationDate) && (
          <p className="mt-1.5 line-clamp-1 text-[14px] text-stone-500 dark:text-stone-400">
            {[
              source.author,
              source.publicationDate
                ? DateFns.format(source.publicationDate, "MMM d, yyyy")
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        )}
        <CardMeta>
          <SourceStatusTag source={source as WebSource} />
          {source.type && (
            <span className="uppercase tracking-wide opacity-70">{source.type}</span>
          )}
          {host && !titleIsHostOnly && (
            <span className="truncate opacity-70">{host}</span>
          )}
        </CardMeta>
      </div>
    </MoveCard>
  )
}
